import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  CloseOutlined,
  EditOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import {
  ContextButton,
  GrayButton,
  IconContextButton,
  LinkButton,
  ScopeButton,
  YellowButton,
} from '@monorepo/shared-lib';
import { List, Popover, Space, Spin, Typography } from 'antd';
import AmexIcon from 'assets/images/icons/amexIcon.svg';
import { ReactComponent as CheckIcon } from 'assets/images/icons/checkIcon.svg';
import DinersIcon from 'assets/images/icons/dinersIcon.svg';
import DiscoverIcon from 'assets/images/icons/discoverIcon.svg';
import { ReactComponent as EllipseIcon } from 'assets/images/icons/ellipsesIcon.svg';
import { ReactComponent as EnterpriseIcon } from 'assets/images/icons/iconEnterprise.svg';
import { ReactComponent as LargeIcon } from 'assets/images/icons/iconLarge.svg';
import { ReactComponent as MediumIcon } from 'assets/images/icons/iconMedium.svg';
import { ReactComponent as SmallIcon } from 'assets/images/icons/iconSmall.svg';
import JcbIcon from 'assets/images/icons/jcbIcon.svg';
import MasterCardIcon from 'assets/images/icons/mastercardIcon.svg';
import UnionPayIcon from 'assets/images/icons/unionpayIcon.svg';
import VisaIcon from 'assets/images/icons/visaIcon.svg';
import UpgradeImage from 'assets/images/upgrade-pic.svg';
import { StyledCard } from 'components/Custom/card';
import { RoundInput } from 'components/Custom/inputs';
import {
  BlackSpan,
  CardDescBoldGraySpan,
  CardDescBoldSpan,
  CardDescGreenSpan,
  CardTitleLargeSpan,
  ColoredTag,
  GrayItalicSpan,
  ListItemSpan,
  TableItemSpan,
} from 'components/Custom/spans';
import { ScopeTable } from 'components/Custom/table';
import { CardInfo } from 'components/OrganisationDetails/card-info';
import { formatDisplayDate, formatDisplayDateRange } from 'i18n/calendarLabels';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { SubscriptionPlan } from 'store/api/enums';
import { MenuSize, PaymentMethod } from 'store/app/enums';
import { getMenuSize } from 'store/app/selectors';
import { BillingLog, Plan } from 'store/billing/models';
import useWindowDimensions from '../../hooks/useWindowDimensions';

const PAYMENT_CARD_CONFIG = {
  [PaymentMethod.VISA.toLowerCase()]: {
    icon: VisaIcon,
    translationKey: 'billing.visa-card',
    altText: 'visa-card',
  },
  [PaymentMethod.MASTERCARD.toLowerCase()]: {
    icon: MasterCardIcon,
    translationKey: 'billing.master-card',
    altText: 'master-card-card',
  },
  [PaymentMethod.AMEX.toLowerCase()]: {
    icon: AmexIcon,
    translationKey: 'billing.amex-card',
    altText: 'amex-card',
  },
  [PaymentMethod.DINERS.toLowerCase()]: {
    icon: DinersIcon,
    translationKey: 'billing.diners-card',
    altText: 'diners-card',
  },
  [PaymentMethod.DISCOVER.toLowerCase()]: {
    icon: DiscoverIcon,
    translationKey: 'billing.discover-card',
    altText: 'discover-card',
  },
  [PaymentMethod.JCB.toLowerCase()]: {
    icon: JcbIcon,
    translationKey: 'billing.jcb-card',
    altText: 'jcb-card',
  },
  [PaymentMethod.UNIONPAY.toLowerCase()]: {
    icon: UnionPayIcon,
    translationKey: 'billing.unionpay-card',
    altText: 'unionpay-card',
  },
};

interface CardIconProps {
  icon: string;
}

const CardIcon: React.FC<CardIconProps> = ({ icon }) => (
  <div
    style={{
      //width: '40px',
      //height: '25px',
      marginRight: 2,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      overflow: 'hidden',
    }}
  >
    <img
      src={icon}
      style={{
        //maxWidth: '100%',
        //maxHeight: '100%',
        width: 55,
        objectFit: 'contain',
        objectPosition: 'left center',
        transformOrigin: 'left center',
      }}
    />
  </div>
);

interface PaymentCardProps {
  cardType?: string;
  last4: string;
  expMonth: string | number;
  expYear: string | number;
  onEdit: () => void;
  compact?: boolean;
}

const PaymentCard: React.FC<PaymentCardProps> = ({
  cardType,
  last4,
  expMonth,
  expYear,
  onEdit,
  compact = false,
}) => {
  const { t, i18n } = useTranslation();
  const cardConfig = cardType
    ? PAYMENT_CARD_CONFIG[cardType.toLowerCase()]
    : null;

  const ExpiryInfo = () => (
    <ListItemSpan style={{ marginLeft: 0 }} fontSize="11px" $lineHeight="29px">
      {t('billing.expires')} {`${expMonth}/${expYear}`}
    </ListItemSpan>
  );

  const GenericCardInfo = ({ withEdit = true }) => (
    <>
      <TableItemSpan fontSize="14px" fontWeight="500" $lineHeight="29px">
        {t('billing.card-label')}{' '}
      </TableItemSpan>
      <GrayItalicSpan $lineHeight="29px">
        {t('billing.ending-in')}{' '}
      </GrayItalicSpan>{' '}
      <TableItemSpan fontSize="14px" fontWeight="500" $lineHeight="29px">
        {last4}
      </TableItemSpan>
      {withEdit && (
        <EditOutlined
          style={{ marginLeft: 32, color: 'var(--color-text-muted, #5a6a6a)' }}
          role="button"
          tabIndex={0}
          aria-label={t('common.edit')}
          onClick={onEdit}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onEdit();
            }
          }}
        />
      )}
    </>
  );

  // If we don't have card info or it's not a known card type
  if (!cardConfig) {
    return compact ? (
      <>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 16,
            width: '100%',
          }}
        >
          <div
            style={{
              display: 'flex',
              columnGap: 4,
              marginTop: 3,
              flexWrap: 'nowrap',
            }}
          >
            <GenericCardInfo withEdit={false} />
          </div>
          <EditOutlined
            style={{ color: 'var(--color-text-muted, #5a6a6a)' }}
            role="button"
            tabIndex={0}
            aria-label={t('common.edit')}
            onClick={onEdit}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onEdit();
              }
            }}
          />
        </div>
        <ExpiryInfo />
      </>
    ) : (
      <div
        style={{
          display: 'flex',
          columnGap: 4,
          marginTop: 3,
          flexWrap: 'wrap',
        }}
      >
        <GenericCardInfo />
        <ExpiryInfo />
      </div>
    );
  }

  // If we have card info, show the full card display
  return compact ? (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          width: '100%',
          padding: '5px 0px',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 5,
          }}
        >
          <CardIcon icon={cardConfig.icon} />
          <TableItemSpan fontSize="14px" fontWeight="500" $lineHeight="29px">
            {t(cardConfig.translationKey)}{' '}
          </TableItemSpan>{' '}
          <GrayItalicSpan $lineHeight="29px" style={{ whiteSpace: 'nowrap' }}>
            {t('billing.ending-in')}{' '}
          </GrayItalicSpan>{' '}
          <TableItemSpan fontSize="13px" fontWeight="500" $lineHeight="29px">
            {last4}
          </TableItemSpan>
        </div>
        <EditOutlined
          style={{ color: 'var(--color-text-muted, #5a6a6a)', marginLeft: 16 }}
          role="button"
          tabIndex={0}
          aria-label={t('common.edit')}
          onClick={onEdit}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onEdit();
            }
          }}
        />
      </div>
      <ExpiryInfo />
    </>
  ) : (
    <div
      style={{
        display: 'flex',
        columnGap: 4,
        marginTop: 3,
        flexWrap: 'wrap',
      }}
    >
      <CardIcon icon={cardConfig.icon} />
      <TableItemSpan fontSize="14px" fontWeight="500" $lineHeight="29px">
        {t(cardConfig.translationKey)}
      </TableItemSpan>{' '}
      <GrayItalicSpan $lineHeight="29px">
        {t('billing.ending-in')}{' '}
      </GrayItalicSpan>
      <TableItemSpan fontSize="14px" fontWeight="500" $lineHeight="29px">
        {last4}
      </TableItemSpan>
      <EditOutlined
        style={{ marginLeft: 10, color: 'var(--color-text-muted, #5a6a6a)' }}
        role="button"
        tabIndex={0}
        aria-label={t('common.edit')}
        onClick={onEdit}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onEdit();
          }
        }}
      />
      <ExpiryInfo />
    </div>
  );
};

import { Invoice } from 'store/billing/models/invoice';
import { getCurrencySymbol } from 'utils/calcAmountCurrency';
import { useNumberFormat } from 'utils/numberFormat';

import moment from 'moment';
import { CurrentSubscription } from 'store/billing/models/currentSubscription';
import { SubscriptionType } from 'store/billing/models/subscriptionType';
import { getNextPlanPrice } from 'utils/getNextPlanPrice';
import { findSubscriptionPlanType } from 'utils/subscriptionPlanType';

interface BillingComponentProps {
  backToChangePlan: () => void;
  getFeaturesOfPlans: () => Plan[];
  editPlan: () => void;
  redirectPage: (url: string) => void;
  renderCancelPlanWindow: () => void;
  updateCard: () => void;
  currentSubscription: CurrentSubscription;
  invoices: Invoice[];
  isLoading: boolean;
  subscriptionPlanTypes: SubscriptionType[];
  checkoutToUpgradePlan: (typeId: string) => void;
  renderContactUsWindow: (isEnterprise: boolean) => void;
  downgradePlan: (plan: string) => void;
}

const billingLogs: BillingLog[] = [
  {
    InvoiceId: '1',
    invoiceNumber: 'INV 123456',
    invoiceDate: '14/02/2025',
    status: 'billed',
    amount: '€300.00',
  },
  {
    InvoiceId: '2',
    invoiceNumber: 'INV 123789',
    invoiceDate: '18/08/2025',
    status: 'cancelled',
    amount: '€500.00',
  },
];
const pricingPageLink = '/pricing';
const PLAN_OVERVIEW_BREAKPOINT = 1500;

export const BillingComponent: React.FC<BillingComponentProps> = ({
  backToChangePlan,
  getFeaturesOfPlans,
  editPlan,
  currentSubscription,
  redirectPage,
  renderCancelPlanWindow,
  invoices,
  updateCard,
  isLoading,
  subscriptionPlanTypes,
  checkoutToUpgradePlan,
  renderContactUsWindow,
  downgradePlan,
}) => {
  const { t, i18n } = useTranslation();
  const { formatNumber } = useNumberFormat();
  const { width: windowWidth } = useWindowDimensions();
  const menuSize = useSelector(getMenuSize);
  const [dataSource, setDataSource] = React.useState<Invoice[]>();
  const [showPlanOverview, setShowPlanOverview] = React.useState(false);
  /* const [paymentMethod, setPaymentMethod] = React.useState(
    PaymentMethod.MASTERCARD
  ); */

  const getPlanIcon = (currentPlan) => {
    switch (currentPlan) {
      case SubscriptionPlan.SMALL_PLAN:
        return (
          <SmallIcon
            style={{
              borderRadius: '50px',
            }}
          />
        );
        break;
      case SubscriptionPlan.MEDIUM_PLAN:
        return (
          <MediumIcon
            style={{
              borderRadius: '50px',
            }}
          />
        );
        break;
      case SubscriptionPlan.LARGE_PLAN:
        return (
          <LargeIcon
            style={{
              borderRadius: '50px',
            }}
          />
        );
        break;
      case SubscriptionPlan.ENTERPRISE_PLAN:
        return (
          <EnterpriseIcon
            style={{
              borderRadius: '50px',
            }}
          />
        );
        break;
      default:
        break;
    }
  };

  React.useEffect(() => {
    setDataSource(invoices);
  }, [invoices]);

  const upgradePlan = (planName: string) => {
    const currentPlan = findSubscriptionPlanType(
      subscriptionPlanTypes,
      planName
    );
    currentPlan?.uuid && checkoutToUpgradePlan(currentPlan.uuid);
  };

  const renderPaymentMethod = () => {
    let defaultPaymentMethod = currentSubscription?.paymentMethodDetails.find(
      (method) => method.default
    );
    defaultPaymentMethod = defaultPaymentMethod
      ? defaultPaymentMethod
      : currentSubscription?.paymentMethodDetails[0];

    if (!defaultPaymentMethod) {
      return null;
    }

    return (
      <PaymentCard
        cardType={defaultPaymentMethod.brand}
        last4={defaultPaymentMethod.last4}
        expMonth={defaultPaymentMethod.expMonth}
        expYear={defaultPaymentMethod.expYear}
        onEdit={updateCard}
        compact={
          defaultPaymentMethod.brand?.toLowerCase() !==
          PaymentMethod.MASTERCARD.toLowerCase()
        }
      />
    );
  };

  const renderPlanOverviewCard = () => {
    return (
      <Spin spinning={isLoading}>
        <StyledCard width="320px" style={{ marginTop: '30px' }}>
          <div
            style={{
              margin: '10px 0px 20px 0px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <TableItemSpan fontSize="18px" fontWeight="400" $lineHeight="24px">
              {t('billing.plan-overview')}
            </TableItemSpan>
            {windowWidth <= PLAN_OVERVIEW_BREAKPOINT && (
              <CloseOutlined
                role="button"
                tabIndex={0}
                aria-label={t('common.close')}
                onClick={() => setShowPlanOverview(false)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setShowPlanOverview(false);
                  }
                }}
              />
            )}
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              paddingBottom: '25px',
              marginBottom: '20px',
              borderBottom: '1px solid var(--color-border, #dbeae3)',
              flexDirection: 'column',
              rowGap: 10,
              width: '100%',
            }}
          >
            <CardInfo
              label={t('billing.plan-name')}
              data={
                currentSubscription
                  ? `${currentSubscription?.description} ${t('billing.plan-suffix')}`
                  : ''
              }
              horizontal
            />
            <CardInfo
              label={t('billing.billing-frequency')}
              data={
                currentSubscription ? currentSubscription?.billingFrequency : ''
              }
              horizontal
            />
            <CardInfo
              label={t('billing.next-billing-date')}
              data={
                currentSubscription?.endDate
                  ? formatDisplayDate(
                      currentSubscription?.endDate,
                      'DD/MM/YYYY',
                      i18n.language
                    )
                  : t('billing.not-available')
              }
              horizontal
            />
            <CardInfo
              label={t('billing.amount')}
              data={
                currentSubscription
                  ? `${getCurrencySymbol(
                      //@ts-ignore
                      currentSubscription?.currency?.toUpperCase()
                    )} ${formatNumber(
                      currentSubscription?.upcomingInvoice?.totalAmount ?? 0
                    )}`
                  : ''
              }
              horizontal
            />
            <CardInfo
              label={t('billing.payment-method')}
              data={renderPaymentMethod()}
            />
          </div>
          <div
            style={{
              paddingBottom: '10px',
              marginBottom: '10px',
              width: '100%',
            }}
          >
            <TableItemSpan fontSize="16px" fontWeight="500" $lineHeight="22px">
              {t('billing.add-ons')}
            </TableItemSpan>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginTop: 10,
              }}
            >
              <ListItemSpan $lineHeight="22px">
                {' '}
                {t('navigation.vat-number-checker')}{' '}
                <TableItemSpan fontWeight="600" $lineHeight="22px">
                  x{currentSubscription?.vatNumberChecker}
                </TableItemSpan>
              </ListItemSpan>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <ListItemSpan $lineHeight="22px">
                {' '}
                {t('billing.additional-entities')}{' '}
                <TableItemSpan fontWeight="600" $lineHeight="22px">
                  x{currentSubscription?.additionalOrganisations}
                </TableItemSpan>
              </ListItemSpan>
            </div>
          </div>

          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              gap: '12px',
              margin: '0 12px',
              marginTop: '18px',
              justifyContent: 'center',
            }}
          >
            <ScopeButton
              type="primary"
              width="136px"
              height="32px"
              onClick={editPlan}
            >
              {t('billing.edit-plan')}
            </ScopeButton>
            <ScopeButton
              type="default"
              width="136px"
              height="32px"
              onClick={renderCancelPlanWindow}
            >
              {t('billing.cancel-plan')}
            </ScopeButton>
          </div>
        </StyledCard>
      </Spin>
    );
  };

  const renderUpgradeButton = (
    plan: Plan,
    currentPlanIndex: number,
    index: number
  ) => {
    if (
      plan.planName?.toLowerCase() ==
      SubscriptionPlan.ENTERPRISE_PLAN.toLowerCase()
    ) {
      return (
        <ScopeButton
          type="primary"
          height="32px"
          width="123px"
          fontSize="13px"
          lineheight="22px"
          onClick={() => renderContactUsWindow(true)}
        >
          {t('billing.contact-us')}
        </ScopeButton>
      );
    } else {
      if (currentPlanIndex > index) {
        return (
          <ScopeButton
            type="primary"
            height="32px"
            width="130px"
            fontSize="13px"
            lineheight="22px"
            onClick={() => {
              const currentPlan = findSubscriptionPlanType(
                subscriptionPlanTypes,
                plan.planName
              );
              currentPlan?.uuid && downgradePlan(currentPlan.uuid);
            }}
          >
            {t('billing.downgrade-plan')}
          </ScopeButton>
        );
      } else if (currentPlanIndex < index) {
        return (
          <ScopeButton
            type="primary"
            height="32px"
            width="123px"
            fontSize="13px"
            lineheight="22px"
            onClick={() => upgradePlan(plan.planName)}
          >
            {t('billing.upgrade-plan')}
          </ScopeButton>
        );
      }
    }
  };

  const renderPlanTypeCard = () => {
    const currentPlanIndex = getFeaturesOfPlans().findIndex(
      (plan) =>
        plan.planName.toLowerCase() ==
        currentSubscription?.description?.toLowerCase()
    );
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: '25px',
          columnGap: 20,
        }}
      >
        {getFeaturesOfPlans().map((plan, index) => (
          <StyledCard
            key={plan.planName}
            className={
              plan.planName
                .toLowerCase()
                .includes(currentSubscription?.description?.toLowerCase())
                ? 'selected-plan-container'
                : 'non-selected-container'
            }
            width="270px"
            height="320px"
            style={{
              overflow: 'auto',
            }}
          >
            <Space
              direction="vertical"
              style={{
                width: '100%',
                alignItems: 'center',
                marginBottom: '20px',
              }}
            >
              {getPlanIcon(plan.planName)}
              <TableItemSpan
                fontSize="16px"
                fontWeight="400"
                $lineHeight="24px"
              >
                {plan.planName}
              </TableItemSpan>
              <ListItemSpan $lineHeight="24px">
                {t('billing.connected-organisations')}
              </ListItemSpan>
              <TableItemSpan
                fontSize="26px"
                fontWeight="400"
                $lineHeight="40px"
              >
                {plan.connectedOrganisations}
              </TableItemSpan>
            </Space>
            <Space
              direction="vertical"
              style={{ width: '100%', alignItems: 'center' }}
            >
              {plan.planName
                .toLowerCase()
                .includes(currentSubscription?.description?.toLowerCase()) ? (
                <GrayButton
                  height="32px"
                  width="123px"
                  fontSize="13px"
                  lineheight="22px"
                  disabled
                >
                  {t('billing.current-plan')}
                </GrayButton>
              ) : (
                renderUpgradeButton(plan, currentPlanIndex, index)
              )}
              <LinkButton
                type="link"
                color='var(--color-primary, #41aaaf)'
                onClick={backToChangePlan}
              >
                {t('billing.more-details')}
              </LinkButton>
            </Space>
          </StyledCard>
        ))}
      </div>
    );
  };

  const renderStatus = (content, record) => {
    if (content == 'cancelled') {
      return (
        <ColoredTag
          color="var(--color-status-danger, #D13131)"
          bgcolor="White"
          bordered={true}
          bordercolor="var(--color-border, #dbeae3)"
          $smaller={false}
        >
          {t('billing.cancelled')}
        </ColoredTag>
      );
    } else if (content == 'paid') {
      return (
        <ColoredTag
          color='var(--color-primary, #41aaaf)'
          bgcolor="White"
          bordered={true}
          bordercolor="var(--color-border, #dbeae3)"
          $smaller={false}
        >
          {t('billing.billed')}
        </ColoredTag>
      );
    } else if (content == 'open') {
      return (
        <ColoredTag
          color="var(--color-status-danger, #D13131)"
          bgcolor="White"
          bordered={true}
          bordercolor="var(--color-border, #dbeae3)"
          $smaller={false}
        >
          {t('main.open')}
        </ColoredTag>
      );
    }
  };

  const renderAmountWithCurrency = (content, record: Invoice) => {
    return (
      <span>{`${getCurrencySymbol(
        //@ts-ignore
        record.currency.toUpperCase()
      )} ${content}`}</span>
    );
  };

  const renderSortIcon = ({ sortOrder }) => {
    if (sortOrder == 'ascend') {
      return <ArrowDownOutlined style={{ color: 'var(--color-text-muted, #5a6a6a)' }} />;
    } else {
      return <ArrowUpOutlined style={{ color: 'var(--color-text-muted, #5a6a6a)' }} />;
    }
  };

  const renderContextMenu = (record: Invoice) => {
    return (
      <div className="context-menu-items">
        <Space direction="vertical" style={{ rowGap: '0' }}>
          <IconContextButton type="text">
            <a
              href={record.hostedInvoiceUrl}
              style={{ textDecoration: 'none' }}
              target="_blank"
              rel="noreferrer"
            >
              {t('billing.view-invoice')}
            </a>
          </IconContextButton>

          <IconContextButton type="text">
            <a
              href={record.invoicePdf}
              style={{ textDecoration: 'none' }}
              target="_blank"
              rel="noreferrer"
            >
              {t('billing.download-pdf')}
            </a>
          </IconContextButton>
          {/* <IconContextButton type="text" disabled={!record.receiptPdf}>
            <a
              href={record.receiptPdf || '#'}
              style={{
                textDecoration: 'none',
                pointerEvents: !record.receiptPdf ? 'none' : 'auto',
                opacity: !record.receiptPdf ? 0.5 : 1,
              }}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => {
                if (!record.receiptPdf) {
                  e.preventDefault();
                }
              }}
            >
              {t('billing.download-receipt')}
            </a>
          </IconContextButton> */}
        </Space>
      </div>
    );
  };

  const columns = [
    {
      title: t('billing.invoice-number'),
      dataIndex: 'invoiceNumber',
      key: 'invoiceNumber',
      sorter: (a, b) => a.invoiceNumber?.localeCompare(b.invoiceNumber),
      sortIcon: renderSortIcon,
    },
    {
      title: t('billing.invoice-period'),
      dataIndex: 'period_start',
      key: 'period_start',
      render: (content, record: Invoice) => {
        return (
          content &&
          formatDisplayDateRange(
            record.period_start,
            record.period_end,
            'DD/MM/YYYY',
            i18n.language
          )
        );
      },
      sorter: (a, b) => a.invoiceDate?.localeCompare(b.invoiceDate),
      sortIcon: renderSortIcon,
    },
    {
      title: t('billing.invoice-date'),
      dataIndex: 'created',
      key: 'created',
      render: (content, record: Invoice) => {
        return content && formatDisplayDate(content, 'DD/MM/YYYY', i18n.language);
      },
      sorter: (a, b) => a.invoiceDate?.localeCompare(b.invoiceDate),
      sortIcon: renderSortIcon,
    },
    {
      title: t('main.status'),
      dataIndex: 'status',
      key: 'status',
      render: renderStatus,
      sorter: (a, b) => a.status?.localeCompare(b.status),
      sortIcon: renderSortIcon,
    },
    {
      title: t('billing.amount'),
      dataIndex: 'amountDue',
      key: 'amountDue',
      render: renderAmountWithCurrency,
      sorter: (a, b) => a.amountDue?.localeCompare(b.amountDue),
      sortIcon: renderSortIcon,
    },

    {
      key: 'action',
      //width: '140px',
      render: (_, record) => {
        if (record.status != 'paid') {
          return (
            <ScopeButton
              type="primary"
              width="90px"
              height="30px"
              lineheight="18px"
              fontSize="13px"
            >
              <a
                href={record.hostedInvoiceUrl}
                style={{ textDecoration: 'none' }}
                target="_blank"
                rel="noreferrer"
              >
                {t('billing.pay-now')}
              </a>
            </ScopeButton>
          );
        } else {
          return (
            <Popover
              placement="topLeft"
              arrow={false}
              trigger="click"
              content={renderContextMenu(record)}
              title={''}
              overlayStyle={{ zIndex: 999 }}
              destroyOnHidden={true}
            >
              <ContextButton type="text" aria-label={t('common.more-actions')}>
                <EllipseIcon
                  style={{
                    width: 18,
                    height: 18,
                  }}
                />
              </ContextButton>
            </Popover>
          );
        }
      },
    },
  ];

  const search = (e) => {
    const value = e.target.value;
    if (value != '') {
      const filterTable = invoices?.filter((o) =>
        Object.keys(o).some((k) =>
          String(o[k]).toLowerCase().includes(value.toLowerCase())
        )
      );

      setDataSource(filterTable);
    } else {
      setDataSource(invoices);
    }
  };

  const renderBillingHistoryCard = () => {
    return (
      <StyledCard
        width="100%"
        style={{
          overflow: 'auto',
          marginBottom: '25px',
        }}
      >
        <ScopeTable
          columns={columns}
          mainLocaleKey="users"
          originalDataSource={billingLogs}
          transactionType="billing"
          reportId="billing-history"
          tableDataSource={dataSource}
          rowKey="invoiceId"
          pagination={true}
          rowsPerPage={8}
          enableColumnsCustomization={false}
          rowHeight={45}
          showSizeChanger={false}
          getNewPageSize={() => {}}
          titleComponent={
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              <CardTitleLargeSpan style={{ fontSize: 18, lineHeight: '35px' }}>
                {t('billing.billing-history')}
              </CardTitleLargeSpan>

              <RoundInput
                width={'200px'}
                height={'35px'}
                prefix={<SearchOutlined />}
                size="large"
                placeholder={t('billing.search-bills')}
                onChange={search}
              />
            </div>
          }
        />
      </StyledCard>
    );
  };

  const getNextPlanPriceInfo = () => {
    return getNextPlanPrice(currentSubscription, getFeaturesOfPlans);
  };

  const renderFeaturesCard = () => {
    return (
      <StyledCard
        $borderless
        $stopshadowbox
        $nopadding
        $removeHeader
        width="100%"
        style={{ marginBottom: '25px' }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            flexFlow: 'row',
            alignContent: 'normal',
          }}
        >
          <div
            style={{
              padding: '20px 0px',
              paddingTop: '48px',
              paddingLeft: '105px',
            }}
          >
            <Typography
              style={{
                fontSize: '24px',
                fontWeight: '600',
                lineHeight: '26px',
                textAlign: 'left',
                color: 'var(--color-text-dark, #042a2a)',
                marginBottom: '10px',
              }}
            >
              <CardDescGreenSpan>
                {t('billing.unlock-all-features')}
              </CardDescGreenSpan>
              <br />
              {t('billing.simplify-vat')}, {t('billing.amplify-efficiency')}{' '}
              <br />
              {(() => {
                const nextPlanInfo = getNextPlanPriceInfo();

                if (
                  nextPlanInfo.price !== null &&
                  nextPlanInfo.currency !== null
                ) {
                  // Ensure both price and currency are strings and not objects
                  const priceStr =
                    typeof nextPlanInfo.price === 'object'
                      ? t('billing.contact-us')
                      : String(nextPlanInfo.price);
                  const currencyStr =
                    typeof nextPlanInfo.currency === 'object'
                      ? 'EUR'
                      : String(nextPlanInfo.currency);

                  // Additional safety check - if price is still an object, show fallback
                  if (typeof nextPlanInfo.price === 'object') {
                    console.error(
                      'Price is still an object after extraction:',
                      nextPlanInfo.price
                    );
                    return (
                      <CardDescBoldGraySpan>
                        {t('billing.contact-us-enterprise-pricing')}
                      </CardDescBoldGraySpan>
                    );
                  }

                  return (
                    <CardDescBoldGraySpan>
                      {t('billing.from')}{' '}
                      <CardDescBoldSpan fontSize="16px" fontWeight="700">
                        {priceStr} {currencyStr}{' '}
                      </CardDescBoldSpan>
                      {t('billing.per-month')}
                    </CardDescBoldGraySpan>
                  );
                } else {
                  return (
                    <CardDescBoldGraySpan>
                      {t('billing.contact-us-enterprise-pricing')}
                    </CardDescBoldGraySpan>
                  );
                }
              })()}
            </Typography>
            <List
              style={{ marginBottom: '15px' }}
              dataSource={[
                t('billing.more-vat-functionality'),
                t('billing.connect-more-entities'),
                t('billing.integrations-xero-excel-more'),
              ]}
              split={false}
              size="small"
              renderItem={(item) => (
                <List.Item style={{ padding: '0px 3px' }}>
                  <CheckIcon style={{ width: '15px', height: '15px', marginTop: '2px', flexShrink: 0 }} /> <BlackSpan>{item}</BlackSpan>
                </List.Item>
              )}
            />

            <YellowButton
              type="primary"
              width="139px"
              height="32"
              fontSize="13px"
              className="yellow-btn"
              color="var(--color-text-dark, #042a2a)"
              border_color="#FFBC3E"
              onClick={() => redirectPage(pricingPageLink)}
            >
              {t('billing.upgrade-now')}
            </YellowButton>
          </div>
          <img
            alt={t('billing.upgrade-image-alt')}
            style={{ width: '404px', marginRight: 35 }}
            src={UpgradeImage}
          />
        </div>
      </StyledCard>
    );
  };

  return (
    <Spin spinning={isLoading} size="large">
      <div className="billing-page">
        <div
          //style={{ float: 'left', width: '60%', margin: '0 10%' }}
          style={{
            width:
              windowWidth > PLAN_OVERVIEW_BREAKPOINT
                ? menuSize === MenuSize.WIDE
                  ? 'calc(100vw - 575px)'
                  : 'calc(100vw - 435px)'
                : '100%',
            minWidth: 850,
            marginRight:
              windowWidth < PLAN_OVERVIEW_BREAKPOINT && showPlanOverview
                ? '24px'
                : '0px',
          }}
        >
          <div
            style={{
              padding: '0 40px',
              //height: '100vh',
              //width: '100%',
              margin: 'auto',
              //maxWidth: 1600,
              //minWidth: 1200,
              maxWidth: 1250,
              marginTop: '30px',
            }}
          >
            {currentSubscription?.description !=
              SubscriptionPlan.ENTERPRISE_PLAN &&
              currentSubscription?.description != SubscriptionPlan.LARGE_PLAN &&
              renderFeaturesCard()}
            {renderPlanTypeCard()}
            {renderBillingHistoryCard()}
          </div>
        </div>
        {windowWidth > PLAN_OVERVIEW_BREAKPOINT || showPlanOverview ? (
          <div
            style={{
              height: '100vh',
              position: 'fixed',
              float: 'right',
              right: 40,
              //width: '25%',
            }}
          >
            {renderPlanOverviewCard()}
          </div>
        ) : (
          <div
            style={{
              fontWeight: 'bold',
              fontSize: '12px',
              letterSpacing: '2px',
              background: 'var(--color-primary, #41aaaf)',
              color: '#fff',
              padding: '7px 0px 0px 0px',
              borderRadius: '10px 0px 0px 10px',
              marginTop: '30px',
              lineHeight: '24px',
              height: 'fit-content',
              cursor: 'pointer',
              /* position: 'fixed',
              float: 'right',
              right: 0, */
            }}
            onClick={() => setShowPlanOverview(true)}
          >
            <span style={{ writingMode: 'vertical-rl' }}>
              {t('billing.plan-overview')}
            </span>
          </div>
        )}
      </div>
    </Spin>
  );
};
